import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { formatNaira } from '../lib/data';
import { useProductCatalog } from '../hooks/useProductCatalog';
import { Button, Icon, SiteFooter, SiteHeader, Thumb } from '../components/bx';
import landingGroceries from '../assets/images/landing-groceries.png';
import '../styles/bx.css';
import '../styles/home.css';

interface LandingPageProps {
  onStart: () => void;
  onGetMe: () => void;
  onSubscribe: () => void;
  onLogoClick?: () => void;
}

export default function LandingPage({ onStart, onGetMe, onSubscribe, onLogoClick }: LandingPageProps) {
  const currentMonth = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date());
  const { items: popular, loading } = useProductCatalog({ type: 'product' });

  useSEO({
    title: 'BOQBOX - Quick, discreet delivery in Lagos',
    description: 'Fresh bread, eggs, fruit and water delivered to your gate in a plain bag. Shop once, subscribe, or send a gift.',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    canonicalUrl: 'https://boqbox.ng',
  });

  const paths = [
    { key: 'get', icon: 'bolt', title: 'Get me', text: 'One-time delivery of everyday essentials, fast.', cta: 'Shop now', onClick: onGetMe },
    { key: 'sub', icon: 'repeat', title: 'Subscribe', text: 'Weekly or monthly restocks, so you never run out.', cta: 'See plans', onClick: onSubscribe, badge: 'Most popular' },
    { key: 'gift', icon: 'gift', title: 'Send a gift', text: 'Surprise someone in Lagos — we deliver it for you.', cta: 'Send a gift', onClick: onStart },
  ];

  return (
    <div className="bx bx-page-enter">
      <SiteHeader onLogoClick={onLogoClick} />
      <main className="bx-main" style={{ paddingBottom: 0 }}>
        <section className="bx-home-hero">
          <div className="bx-container bx-home-hero-inner">
            <div className="bx-home-copy">
              <span className="bx-home-eyebrow"><Icon name="pin" size={15} /> Delivering across Lagos</span>
              <h1>Quick, quiet drops. <span>No drama.</span></h1>
              <p>Fresh bread, eggs, fruit and clean water delivered to your gate in a plain, unlabelled bag.</p>
              <div className="bx-home-ctas">
                <Button size="lg" onClick={onGetMe}>Shop essentials <Icon name="arrow" /></Button>
                <Button size="lg" variant="secondary" onClick={onSubscribe}>Start a subscription</Button>
              </div>
              <ul className="bx-home-trust">
                <li><Icon name="shield" size={16} /> Plain, discreet bags</li>
                <li><Icon name="truck" size={16} /> Fast Lagos delivery</li>
                <li><Icon name="lock" size={16} /> Secure Paystack checkout</li>
              </ul>
            </div>
            <div className="bx-home-visual" aria-hidden="true">
              <div className="bx-home-plate"><img src={landingGroceries} alt="" /></div>
              <div className="bx-home-float a"><span>🛵</span><div><strong>On its way</strong><small>Arriving in 45 mins</small></div></div>
              <div className="bx-home-float b"><span>🛍️</span><div><strong>Plain bag</strong><small>No logos, no labels</small></div></div>
            </div>
          </div>
        </section>

        <section className="bx-container bx-home-section">
          <div className="bx-home-paths">
            {paths.map((p) => (
              <button key={p.key} type="button" className={`bx-home-path${p.badge ? ' featured' : ''}`} onClick={p.onClick}>
                {p.badge ? <span className="bx-home-path-badge">{p.badge}</span> : null}
                <span className="bx-home-path-icon"><Icon name={p.icon} size={22} /></span>
                <strong>{p.title}</strong>
                <span className="bx-home-path-text">{p.text}</span>
                <span className="bx-home-path-cta">{p.cta} <Icon name="arrow" size={16} /></span>
              </button>
            ))}
          </div>
        </section>

        <section className="bx-container bx-home-section">
          <div className="bx-between bx-home-head">
            <div>
              <h2>Popular right now</h2>
              <p>What Lagos is ordering this week.</p>
            </div>
            <Button variant="secondary" size="sm" onClick={onGetMe}>View all <Icon name="arrow" size={15} /></Button>
          </div>
          <div className="bx-home-rail">
            {loading && popular.length === 0
              ? Array.from({ length: 4 }, (_, i) => <div key={i} className="bx-skel bx-home-rail-card" style={{ height: 230 }} />)
              : popular.slice(0, 8).map((item) => (
                  <button key={item._id} type="button" className="bx-home-rail-card bx-product" onClick={onGetMe}>
                    <Thumb src={item.image_url} category={item.category} alt={item.name} />
                    <div className="bx-product-body">
                      <div className="bx-product-name">{item.name}</div>
                      <div className="bx-product-foot" style={{ zIndex: 0 }}>
                        <span className="bx-price">{formatNaira(item.price || 0)}</span>
                        <span className="bx-pill brand">Shop</span>
                      </div>
                    </div>
                  </button>
                ))}
          </div>
        </section>

        <section className="bx-container bx-home-section">
          <div className="bx-home-head"><h2>How it works</h2><p>Three steps, zero stress.</p></div>
          <ol className="bx-home-steps">
            {[
              ['Pick what you need', 'Browse essentials or choose a subscription plan that fits your home.'],
              ['Choose a vendor & pay', 'Pick the store we shop from, then pay securely with Paystack.'],
              ['We drop it quietly', 'A BOQBOX rider delivers in a plain bag — no logos, no fuss.'],
            ].map(([title, text], i) => (
              <li key={title} className="bx-card bx-card-pad">
                <span className="bx-home-step-n">{i + 1}</span>
                <strong>{title}</strong>
                <span>{text}</span>
              </li>
            ))}
          </ol>
        </section>

        <section className="bx-container bx-home-section">
          <div className="bx-home-duo">
            <button type="button" className="bx-home-banner dark" onClick={onSubscribe}>
              <span className="bx-pill" style={{ background: 'rgba(255,255,255,.12)', color: '#fff' }}>For street resellers</span>
              <strong>Quiet Stock Peace</strong>
              <span>Daily snacks and drinks delivered straight to your selling spot.</span>
              <span className="bx-home-path-cta">Explore B2B plans <Icon name="arrow" size={16} /></span>
            </button>
            <Link to="/offers" className="bx-home-banner brand">
              <span className="bx-pill" style={{ background: 'rgba(255,255,255,.2)', color: '#fff' }}>Events & conferences</span>
              <strong>Special meal offers</strong>
              <span>Book breakfast, lunch and dinner for your event — delivered or ready for pickup.</span>
              <span className="bx-home-path-cta">See meal offers <Icon name="arrow" size={16} /></span>
            </Link>
          </div>
          <div className="bx-home-promo">
            <span aria-hidden="true">🔥</span>
            <p><strong>First 100 subscribers this {currentMonth}</strong> get ₦2,000 off + free delivery.</p>
            <Button size="sm" onClick={onSubscribe}>Claim offer</Button>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
